import React, { useState } from "react";
import useSortableData from "../../utils/SortData";
import {
  MynauiEditOne,
  TablerCaretUpDownFilled,
} from "../../assets/icons/icons";
import { Check, X } from "lucide-react";
import { updateSellingPrice } from "../../actions/ProductAction";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EmptyTableRecord from "../layout/EmptyTableRecord";
import { useSession } from "../layout/SessionContext";

const TABLE_HEADERS = [
  { key: "skuCode", label: "SKU" },
  { key: "name", label: "Product name" },
  { key: "category", label: "Category" }, // was "category.name"
  { key: "brand.name", label: "Brand" },
  { key: "price", label: "Face value(€)" }, // was "faceValue"
  { key: "buyingPrice", label: "Buying Price(€)" },
  { key: "sellingPrice", label: "Selling Price(€)" },
  { key: "serviceFee", label: "Service Fee(€)" },
  { key: "earning", label: "Earnings(€)" }, // was "earnings"
  { key: "action", label: "Action", isAction: true },
];

const ProductTable = ({ products, setProducts, onRefetch }) => {
  const { hasPermission } = useSession();
  const { sortedItems: sortedProducts, handleSort } = useSortableData(products);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingSellingPrice, setEditingSellingPrice] = useState("");
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [isClosingPrice, setIsClosingPrice] = useState(false);

  const selectedColumns = TABLE_HEADERS.map((h) => h.key);

  const formatFaceValue = (product) => {
    const sp = product.price;
    return sp ? parseFloat(sp?.$numberDecimal || sp).toFixed(2) : "0.00";
  };

  const formatBuyingPrice = (product) => {
    const sp = product.buyingPrice;
    return sp ? parseFloat(sp?.$numberDecimal || sp).toFixed(2) : "0.00";
  };

  const formatSellingPrice = (product) => {
    const sp = product.sellingPrice;
    return sp ? parseFloat(sp?.$numberDecimal || sp).toFixed(2) : "0.00";
  };

  const handleStartEditSellingPrice = (product) => {
    const id = String(product._id || product.id || "");
    if (!id) return;
    setEditingProductId(id);
    setEditingSellingPrice(formatSellingPrice(product));
  };

  const handleCancelSellingPrice = () => {
    setIsClosingPrice(true);
    setTimeout(() => {
      setEditingProductId(null);
      setEditingSellingPrice("");
      setIsClosingPrice(false);
    }, 180);
  };

  const handleSellingPriceInputChange = (value) => {
    if (value === "") {
      setEditingSellingPrice("");
      return;
    }

    if (/^\d*(\.\d{0,2})?$/.test(value)) {
      setEditingSellingPrice(value);
    }
  };

  const handleConfirmSellingPrice = async (productId) => {
    const parsed = parseFloat(editingSellingPrice);
    if (isNaN(parsed) || parsed < 0) {
      toast.error("Please enter a valid selling price.");
      return;
    }
    setIsUpdatingPrice(true);
    try {
      const response = await updateSellingPrice(productId, parsed);
      if (!response) return;
      if (response.success) {
        const updatedPrice = response.data?.productSellingPrice ?? parsed;
        const productToUpdate = products.find(
          (p) => String(p._id || p.id || "") === String(productId),
        );
        const faceValue = productToUpdate
          ? parseFloat(
              productToUpdate.price?.$numberDecimal ||
                productToUpdate.price ||
                0,
            )
          : 0;
        const buyingPrice = productToUpdate
          ? parseFloat(
              productToUpdate.buyingPrice?.$numberDecimal ||
                productToUpdate.buyingPrice ||
                0,
            )
          : 0;
        const newServiceFee = parsed - faceValue;
        const newEarning = parsed - buyingPrice;
        setProducts((prev) =>
          prev.map((p) =>
            String(p._id || p.id || "") === String(productId)
              ? {
                  ...p,
                  sellingPrice: updatedPrice,
                  serviceFee: newServiceFee,
                  earning: newEarning,
                }
              : p,
          ),
        );
        toast.success(response.message || "Selling price updated successfully");
        setEditingProductId(null);
        setEditingSellingPrice("");
      } else {
        toast.error(response.message || "Failed to update selling price.");
      }
    } catch (err) {
      toast.error(
        "Something went wrong while updating selling price. Please try again.",
      );
    } finally {
      setIsUpdatingPrice(false);
    }
  };

  // Helper function to safely access nested properties
  const getNestedValue = (obj, path) => {
    const keys = path.split(".");
    return keys.reduce((acc, key) => {
      return acc && acc[key] !== undefined ? acc[key] : null;
    }, obj);
  };

  // Live-compute service fee and earning from the current editing input
  const computeLiveServiceFee = (product) => {
    const sp = parseFloat(editingSellingPrice) || 0;
    const faceValue = parseFloat(
      product.price?.$numberDecimal || product.price || 0,
    );
    return (sp - faceValue).toFixed(2);
  };

  const computeLiveEarning = (product) => {
    const sp = parseFloat(editingSellingPrice) || 0;
    const buyingPrice = parseFloat(
      product.buyingPrice?.$numberDecimal || product.buyingPrice || 0,
    );
    return (sp - buyingPrice).toFixed(2);
  };

  return (
    <div className="rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto mb-6">
      {/* Desktop view */}
      <div className="hidden md:block">
        <table className="w-full border-gray-300 rounded-md">
          <thead className="bg-gray-100 border-b">
            <tr className="bg-gray-100 md:h-10">
              {TABLE_HEADERS.filter((header) =>
                selectedColumns?.includes(header.key),
              ).map((header, index) => {
                if (header.isAction && !hasPermission("Products", "edit")) {
                  return null;
                }
                return (
                  <th
                    key={index}
                    className="text-center whitespace-nowrap px-2 text-gray-900 text-[15px] font-['Rubik'] font-semibold md:h-10"
                  >
                    {!header.isAction && !header.isToggle ? (
                      <div
                        className="flex items-center cursor-pointer text-left"
                        onClick={() => handleSort(header.key)}
                      >
                        <TablerCaretUpDownFilled className="mr-2" />
                        {header.label}
                      </div>
                    ) : (
                      <div className="flex text-left items-center mr-2">
                        {header.label}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {!sortedProducts || sortedProducts.length === 0 ? (
              <tr>
                <td
                  colSpan={selectedColumns?.length || TABLE_HEADERS.length}
                  className="text-center text-gray-500 py-4"
                >
                  <EmptyTableRecord />
                </td>
              </tr>
            ) : (
              sortedProducts.map((product) => (
                <tr key={product._id} className="hover:bg-gray-50 md:h-[60px]">
                  {TABLE_HEADERS.filter((header) =>
                    selectedColumns?.includes(header.key),
                  ).map((header) => {
                    if (header.isAction && !hasPermission("Products", "edit")) {
                      return null;
                    }
                    return (
                      <td
                        key={header.key}
                        className="px-2 text-left text-gray-700 text-[15px] font-['Rubik'] font-normal md:h-[60px]"
                      >
                        {header.isAction ? (
                          editingProductId &&
                          editingProductId ===
                            String(product._id || product.id || "") ? (
                            <div
                              className={`flex justify-left items-center space-x-1 ${isClosingPrice ? "slide-out-price" : "slide-in-price"}`}
                            >
                              <button
                                className="p-1 flex items-center justify-center btn-confirm text-green-500"
                                onClick={() =>
                                  handleConfirmSellingPrice(editingProductId)
                                }
                                disabled={isUpdatingPrice || isClosingPrice}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                className="p-1 flex items-center justify-center btn-cancel"
                                onClick={handleCancelSellingPrice}
                                disabled={isUpdatingPrice || isClosingPrice}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-left items-center space-x-1 text-red-500">
                              <button
                                className="p-1 rounded"
                                onClick={() =>
                                  handleStartEditSellingPrice(product)
                                }
                              >
                                <MynauiEditOne className="text-gray-400" />
                              </button>
                            </div>
                          )
                        ) : header.key === "sellingPrice" ? (
                          editingProductId &&
                          editingProductId ===
                            String(product._id || product.id || "") ? (
                            <div
                              className={`w-[90px] ${isClosingPrice ? "slide-out-price" : "slide-in-price"}`}
                            >
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editingSellingPrice}
                                onChange={(e) =>
                                  handleSellingPriceInputChange(e.target.value)
                                }
                                className="w-full bg-white font-['Rubik'] font-normal p-1.5 rounded-[4px] text-[15px] outline outline-1 outline-[#D1D5DB] outline-offset-[-1px] text-[#374151] leading-5"
                                disabled={isUpdatingPrice || isClosingPrice}
                                autoFocus
                              />
                            </div>
                          ) : (
                            formatSellingPrice(product)
                          )
                        ) : header.key === "serviceFee" ? (
                          editingProductId &&
                          editingProductId ===
                            String(product._id || product.id || "") ? (
                            computeLiveServiceFee(product)
                          ) : product.serviceFee ? (
                            parseFloat(
                              product.serviceFee?.$numberDecimal ||
                                product.serviceFee,
                            ).toFixed(2)
                          ) : (
                            "0.00"
                          )
                        ) : header.key === "earning" ? (
                          editingProductId &&
                          editingProductId ===
                            String(product._id || product.id || "") ? (
                            computeLiveEarning(product)
                          ) : product.earning ? (
                            parseFloat(
                              product.earning?.$numberDecimal ||
                                product.earning,
                            ).toFixed(2)
                          ) : (
                            "0.00"
                          )
                        ) : header.key === "price" ? (
                          formatFaceValue(product)
                        ) : header.key === "buyingPrice" ? (
                          formatBuyingPrice(product)
                        ) : header.key.includes(".") ? (
                          getNestedValue(product, header.key)
                        ) : (
                          product[header.key] || 0
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile view */}
      <div className="block md:hidden">
        {!sortedProducts || sortedProducts.length === 0 ? (
          <EmptyTableRecord />
        ) : (
          sortedProducts.map((product) => (
            <table
              key={product._id}
              className="w-full border border-gray-200 rounded-none bg-white shadow-sm "
            >
              <tbody>
                {TABLE_HEADERS.filter((header) =>
                  selectedColumns?.includes(header.key),
                ).map((header) => {
                  if (header.isAction && !hasPermission("Products", "edit")) {
                    return null;
                  }
                  let label = header.label;
                  let value = null;
                  if (header.isAction) {
                    value =
                      editingProductId &&
                      editingProductId ===
                        String(product._id || product.id || "") ? (
                        <div
                          className={`flex items-center space-x-1 ${isClosingPrice ? "slide-out-price" : "slide-in-price"}`}
                        >
                          <button
                            className="p-1 flex items-center justify-center btn-confirm  border-green-600 border-[1.80px]"
                            onClick={() =>
                              handleConfirmSellingPrice(editingProductId)
                            }
                            disabled={isUpdatingPrice || isClosingPrice}
                          >
                            <Check size={14} className="text-green-700 " />
                          </button>
                          <button
                            className="p-1 flex items-center justify-center btn-cancel border-red-600 border-[1.80px]"
                            onClick={handleCancelSellingPrice}
                            disabled={isUpdatingPrice || isClosingPrice}
                          >
                            <X size={14} className="text-red-700" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1">
                          <button
                            className="p-1 rounded"
                            onClick={() => handleStartEditSellingPrice(product)}
                          >
                            <MynauiEditOne className="text-gray-400" />
                          </button>
                        </div>
                      );
                  } else if (header.key === "sellingPrice") {
                    value =
                      editingProductId &&
                      editingProductId ===
                        String(product._id || product.id || "") ? (
                        <div
                          className={
                            isClosingPrice
                              ? "slide-out-price"
                              : "slide-in-price"
                          }
                        >
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editingSellingPrice}
                            onChange={(e) =>
                              handleSellingPriceInputChange(e.target.value)
                            }
                            className="w-full bg-white font-['Rubik'] font-normal p-1.5 rounded-[4px] text-[15px] outline outline-1 outline-[#D1D5DB] outline-offset-[-1px] text-[#374151] leading-5"
                            disabled={isUpdatingPrice || isClosingPrice}
                            autoFocus
                          />
                        </div>
                      ) : (
                        formatSellingPrice(product)
                      );
                  } else if (header.key === "serviceFee") {
                    value =
                      editingProductId &&
                      editingProductId ===
                        String(product._id || product.id || "")
                        ? computeLiveServiceFee(product)
                        : product.serviceFee
                          ? parseFloat(
                              product.serviceFee?.$numberDecimal ||
                                product.serviceFee,
                            ).toFixed(2)
                          : "0.00";
                  } else if (header.key === "earning") {
                    value =
                      editingProductId &&
                      editingProductId ===
                        String(product._id || product.id || "")
                        ? computeLiveEarning(product)
                        : product.earning
                          ? parseFloat(
                              product.earning?.$numberDecimal ||
                                product.earning,
                            ).toFixed(2)
                          : "0.00";
                  } else if (header.key === "buyingPrice") {
                    value = formatBuyingPrice(product);
                  } else if (header.key === "price") {
                    value = formatFaceValue(product);
                  } else if (header.key.includes(".")) {
                    value = getNestedValue(product, header.key);
                  } else {
                    value = product[header.key] || 0;
                  }
                  return (
                    <tr key={header.key} className="h-[60px]">
                      <td className="py-2 px-2 text-[16px] font-medium text-gray-900 w-1/2 align-top">
                        {label}
                      </td>
                      <td className="py-2 px-2 text-[16px] font-normal text-gray-700 w-1/2 align-top break-words max-w-[140px] overflow-hidden text-ellipsis">
                        {value}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductTable;
